// src/features/mech/plastics/questions/PartWeightDimensionsQuestion.jsx
import React from "react";
import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PerlTextField from "../../../../shared/components/PerlTextField";

const positiveValueError = (value, unit) => {
  if (value === "" || value == null) return "";
  return Number.isFinite(Number(value)) && Number(value) > 0
    ? ""
    : `Enter a positive, finite value in ${unit}.`;
};

export default function PartWeightDimensionsQuestion({ value, onChange }) {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    white: "#ffffff",
    textPrimary: "#F1F2F3",
    textSecondary: "#9097A1",
  };
  const brandColor =
    theme.custom?.perlColors?.brandColor || theme.palette?.primary?.main || "#ff6034";

  // Parse the value object or initialize with empty strings
  const dimensionValue = value || {
    finishWeight: "",
    partLength: "",
    partWidth: "",
    partHeight: "",
  };

  const weightHelperText = "Enter a positive weight in grams.";
  const dimensionHelperText = "Enter a positive value in mm.";

  const handleFieldChange = (field, newValue) => {
    onChange?.({
      ...dimensionValue,
      [field]: newValue,
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
      <PerlTextField
        label="Finish weight (g)"
        required
        type="number"
        value={dimensionValue.finishWeight || ""}
        onChange={(e) => handleFieldChange("finishWeight", e.target.value)}
        inputProps={{ min: 0 }}
        error={Boolean(positiveValueError(dimensionValue.finishWeight, "grams"))}
        helperText={positiveValueError(dimensionValue.finishWeight, "grams") || weightHelperText}
        sx={{ mt: 1 }}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
      <PerlTextField
        label="Length (mm)"
        required
        type="number"
        value={dimensionValue.partLength || ""}
        onChange={(e) => handleFieldChange("partLength", e.target.value)}
        inputProps={{ min: 0 }}
        error={Boolean(positiveValueError(dimensionValue.partLength, "mm"))}
        helperText={positiveValueError(dimensionValue.partLength, "mm") || dimensionHelperText}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
      <PerlTextField
        label="Width (mm)"
        required
        type="number"
        value={dimensionValue.partWidth || ""}
        onChange={(e) => handleFieldChange("partWidth", e.target.value)}
        inputProps={{ min: 0 }}
        error={Boolean(positiveValueError(dimensionValue.partWidth, "mm"))}
        helperText={positiveValueError(dimensionValue.partWidth, "mm") || dimensionHelperText}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
      <PerlTextField
        label="Height (mm)"
        required
        type="number"
        value={dimensionValue.partHeight || ""}
        onChange={(e) => handleFieldChange("partHeight", e.target.value)}
        inputProps={{ min: 0 }}
        error={Boolean(positiveValueError(dimensionValue.partHeight, "mm"))}
        helperText={positiveValueError(dimensionValue.partHeight, "mm") || dimensionHelperText}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
    </Box>
  );
}

